import { useSyncExternalStore } from "react";
import { loadBank } from "../data/questions";
import { api, ApiError, getToken, setToken, type ApiUser } from "./api";
import { parseProgress } from "./progress";
import { sessionStore } from "./session";
import { progressStore } from "./store";

export type AuthStatus = "idle" | "loading" | "anon" | "authed" | "error";
export interface AuthState { status: AuthStatus; user: ApiUser | null; error?: string }

let state: AuthState = { status: "idle", user: null };
const listeners = new Set<() => void>();

function set(next: AuthState): void {
  state = next;
  for (const l of listeners) l();
}

export const authStore = {
  get: (): AuthState => state,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  /** Dùng trong test để đặt trạng thái trực tiếp. */
  set,
};

export function useAuth(): AuthState {
  return useSyncExternalStore(authStore.subscribe, authStore.get, authStore.get);
}

// ── Đồng bộ tiến độ lên server ───────────────────────────────
let stopSync: (() => void) | null = null;

async function startSync(): Promise<void> {
  const remote = await api.getProgress();
  const local = progressStore.get();
  if (remote.progress) {
    // Server là nguồn chuẩn; giữ lựa chọn giao diện/ngôn ngữ của máy hiện tại.
    progressStore.replace({ ...parseProgress(remote.progress), theme: local.theme, lang: local.lang });
  } else {
    await api.putProgress(local);
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => { void api.putProgress(progressStore.get()).catch(() => {}); };
  const unsub = progressStore.subscribe(() => {
    clearTimeout(timer);
    timer = setTimeout(flush, 1500);
  });
  const onHide = () => { if (document.visibilityState === "hidden" && timer) { clearTimeout(timer); flush(); } };
  document.addEventListener("visibilitychange", onHide);
  stopSync = () => { clearTimeout(timer); unsub(); document.removeEventListener("visibilitychange", onHide); };
}

async function enterAuthed(user: ApiUser): Promise<void> {
  const { questions } = await api.questions();
  loadBank(questions);
  sessionStore.reload();
  await startSync();
  set({ status: "authed", user });
}

async function enterAnon(): Promise<void> {
  const { questions, stats } = await api.publicQuestions();
  loadBank(questions, stats);
  sessionStore.reload();
  set({ status: "anon", user: null });
}

/** Gọi một lần khi mở app: khôi phục phiên đăng nhập hoặc nạp bộ câu công khai. */
export async function bootstrapAuth(): Promise<void> {
  if (state.status !== "idle") return;
  set({ status: "loading", user: null });
  try {
    if (getToken()) {
      try {
        const { user } = await api.me();
        await enterAuthed(user);
        return;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) setToken(null);
        else throw e;
      }
    }
    await enterAnon();
  } catch {
    set({ status: "error", user: null, error: "network" });
  }
}

export async function loginWithGoogle(credential: string): Promise<void> {
  const { token, user } = await api.loginGoogle(credential);
  setToken(token);
  await enterAuthed(user);
}

export async function loginDev(email: string): Promise<void> {
  const { token, user } = await api.loginDev(email);
  setToken(token);
  await enterAuthed(user);
}

/** Đăng xuất: xóa token và tiến độ trên máy này (dữ liệu vẫn còn trên server). */
export async function logout(): Promise<void> {
  stopSync?.();
  stopSync = null;
  setToken(null);
  sessionStore.set(null);
  progressStore.reset();
  await enterAnon().catch(() => set({ status: "anon", user: null }));
}

export async function deleteAccount(): Promise<void> {
  await api.deleteMe();
  await logout();
}
